import React, {FunctionComponent, useMemo, useState} from 'react';
import {Collapse, FormGroup, Switch} from '@blueprintjs/core';

import {OnChangeCallback} from './types';
import {BySpecificHours} from './ByHour';
import Weekdays from './Weekdays';

interface AdvancedOptionsProps {
  byHour: number[] | string[];
  weekdays?: string[];
  showWeekdays?: boolean; // not relevant for `schedule by minute`
  onChange: OnChangeCallback;
}

interface CollapseCardProps {
  open?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

const CollapseCard: FunctionComponent<CollapseCardProps> = ({
  open = false,
  onClose,
  children,
}: CollapseCardProps) => {
  const [isChecked, setIsChecked] = useState(open);

  const onIsCheckedChange = () => {
    const newValue = !isChecked;

    if (!newValue) {
      onClose && onClose();
    }
    setIsChecked(newValue);
  }

  return (
    <div className="collapse-card">
      <FormGroup inline={true}>
        <Switch
          label="Only at specific times"
          checked={isChecked}
          onChange={onIsCheckedChange}
          inline={true}
        />
      </FormGroup>
      <Collapse
        isOpen={isChecked}
        keepChildrenMounted={true}
        transitionDuration={0}
      >
        {isChecked && children}
      </Collapse>
    </div>
  );
};

export const AdvancedOptions = ({
  byHour,
  weekdays,
  showWeekdays = true,
  onChange,
}: AdvancedOptionsProps) => {
  /*
   * Advanced options for Hourly / Minutely schedules
   */
  const _weekdays = weekdays || [];

  const allDaysSelected = useMemo(() => _weekdays.length === 0, [_weekdays.length]);
  const open = useMemo(() => (showWeekdays && _weekdays?.length > 0) || byHour.length > 0, []);

  const transformWeekdays = (weekdays: string[]): string[] => {
    if (weekdays.length === 7) {
      return []; // if all are selected, don't set weekdays as this is the default
    }
    return weekdays;
  }

  const onClose = () => {
    /*
     * unset `byHour` and `weekdays`
     */
    onChange('byHour')([]);

    if (showWeekdays) {
      setTimeout(() => {
        onChange('weekdays')([]);
      }, 100);
    }
  }

  return (
    <CollapseCard open={open} onClose={onClose}>
      <BySpecificHours
        byHour={byHour as any}
        onChange={onChange}
      />
      {
        showWeekdays && (
          <Weekdays
            weekdays={weekdays || []}
            allSelected={allDaysSelected}
            onChange={onChange}
            transformFn={transformWeekdays}
          />
        )
      }
    </CollapseCard>
  )
};

export default AdvancedOptions;
